import React, { useEffect, useState, useMemo } from 'react';
import Select from 'react-select';
// Common Components
import AppBreadCrumb from '../../common/AppBreadCrumb';
import AppToaster from '../../AppToaster';
// API
import { createSalesReturn, fetchSalesReturn, getSalesReturnPrepopulation, getInvoicesForReturn } from '../../../api/SalesReturnsAPI';
import { getParties } from '../../../api/PartiesAPI';
import { getHoas } from '../../../api/HoasAPI';
import { handleApiError } from '../../../api/risapi';
// Hooks & Utils
import { useNavigate } from 'react-router-dom';
import { capitalizeFirstLetter } from '../../../commonfunctions';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from 'date-fns';

const initialItem = { 
    id: null, product_id: null, return_uom_id: null, 
    return_quantity: '', return_price: '', 
    conversion_factor: 1, line_total: 0, 
    adjustments: [], max_return_quantity: null 
};

const initialState = {
    party_id: null,
    sales_invoice_id: null,
    return_date: new Date(),
    reason: '',
    billing_address: '', shipping_address: '',
    items: [], // Start empty until invoice selected
    adjustments: [],
    total_amount: 0,
    source_info: null
};

const SalesReturnForm = ({ formType, currentId, sourceInvoiceId }) => {
    const navigate = useNavigate();
    const [sr, setSr] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isViewOnly, setIsViewOnly] = useState(formType === 'view');
    const [toaster, setToaster] = useState({});
    const [showToaster, setShowToaster] = useState(false);

    // Dropdown Data
    const [partyOptions, setPartyOptions] = useState([]);
    const [invoiceOptions, setInvoiceOptions] = useState([]);
    const [gstOptions, setGstOptions] = useState([]);

    // --- 1. Initial Load (Parties & Masters) ---
    useEffect(() => {
        let isMounted = true;
        if (formType === 'create') setLoading(true); 
        Promise.all([
            new Promise(r => getParties({paginate: false}, r)),
            new Promise(r => getHoas({paginate: false}, r))
        ]).then(([partyRes, hoaRes]) => {
            if (!isMounted) return;
            if (partyRes.parties) {
                 const parties = Array.isArray(partyRes.parties) ? partyRes.parties : partyRes.parties.data;
                 setPartyOptions(parties.map(p => ({value: p.id, label: p.name})));
            }
            if (hoaRes.hoas) {
                 const hoas = Array.isArray(hoaRes.hoas) ? hoaRes.hoas : hoaRes.hoas.data;
                 setGstOptions(hoas.filter(h => h.is_gst_head).map(h => ({value: h.id, label: h.name})));
            }
            setLoading(false);
        });

        // If viewing existing return, load it
        if (currentId && formType === 'view') {
            setLoading(true);
            fetchSalesReturn(currentId, (res) => {
                if (!isMounted) return;
                setLoading(false);
                if (res.sales_return) {
                    const data = res.sales_return;
                    setSr({
                        ...data,
                        return_date: parseISO(data.return_date),
                        items: data.items.map(i => ({
                             ...i,
                             return_quantity: parseFloat(i.return_quantity),
                             return_price: parseFloat(i.return_price),
                             product_name: i.product?.name,
                             uom_name: i.uom?.short_name
                        }))
                    });
                    setIsViewOnly(true);
                    // Pre-load the invoice option for display
                    if (data.sales_invoice) {
                         setInvoiceOptions([{ value: data.sales_invoice.id, label: data.sales_invoice.invoice_number }]);
                    }
                }
            });
        }
        return () => { isMounted = false; };
    }, [formType, currentId]);

    // --- 2. Cascade: Load Invoices when Party changes ---
    useEffect(() => {
        if (!sr.party_id || isViewOnly) return;
        // Avoid reloading if we already have the correct invoice loaded
        if (invoiceOptions.find(opt => opt.value === sr.sales_invoice_id) && invoiceOptions.length === 1) return;

        setLoading(true);
        getInvoicesForReturn(sr.party_id, (invoices) => {
            setLoading(false);
            if (Array.isArray(invoices)) {
                setInvoiceOptions(invoices.map(inv => ({
                    value: inv.id, 
                    label: `${inv.invoice_number} (${inv.invoice_date})`
                })));
            }
        });
    }, [sr.party_id, isViewOnly]);

    // --- 3. Handle Pre-population (from URL or Dropdown) ---
    useEffect(() => {
        // If sourceInvoiceId is passed via URL (e.g. from Invoice List "Create Return" button)
        if (sourceInvoiceId && formType === 'create' && !sr.sales_invoice_id) {
             handleInvoiceChange({ value: sourceInvoiceId });
        }
    }, [sourceInvoiceId, formType]);

    const handleInvoiceChange = (selectedOption) => {
        const invoiceId = selectedOption ? selectedOption.value : null;
        // Reset items if invoice is cleared
        if (!invoiceId) {
             setSr(prev => ({ ...prev, sales_invoice_id: null, items: [], source_info: null }));
             return;
        }

        setLoading(true);
        getSalesReturnPrepopulation(invoiceId, (res) => {
             setLoading(false);
             if (res.prepopulation_data) {
                 const data = res.prepopulation_data;
                 setSr(prev => ({
                     ...prev,
                     party_id: data.party_id, // Ensure party matches invoice
                     sales_invoice_id: data.sales_invoice_id,
                     billing_address: data.billing_address,
                     shipping_address: data.shipping_address,
                     source_info: `Invoice #${data.invoice_number} (${data.invoice_date})`,
                     items: data.items.map(item => ({
                         ...initialItem,
                         product_id: item.product_id,
                         // Assuming backend sends names now, or we rely on IDs if not available yet
                         product_name: item.product_name || `Product #${item.product_id}`, 
                         return_uom_id: item.return_uom_id,
                         uom_name: item.uom_name || 'Unit',
                         return_quantity: 0, // User must enter qty
                         max_return_quantity: item.max_return_quantity,
                         return_price: item.return_price,
                         conversion_factor: item.conversion_factor
                     }))
                 }));
                 if (!selectedOption.label) { 
                      // If loaded via URL, set the invoice option manually so dropdown shows it
                      setInvoiceOptions([{ value: data.sales_invoice_id, label: data.invoice_number }]);
                 }
                 setToaster({ type: 'info', heading: 'Loaded', message: 'Enter quantities to return.' });
                 setShowToaster(true);
             } else if (res.error) {
                  setSr(prev => ({ ...prev, sales_invoice_id: null }));
                  setToaster({ type: 'warning', heading: 'Cannot Return', message: res.error.message });
                  setShowToaster(true);
             }
        });
    };

    // --- 4. Calculations & Updates ---
    const handleItemChange = (index, field, value) => {
        const newItems = [...sr.items];
        
        if (field === 'return_quantity') {
             const max = newItems[index].max_return_quantity;
             if (max !== null && parseFloat(value) > max) {
                  value = max; // Cap at max
                  setToaster({type: 'warning', heading: 'Limit Reached', message: `Maximum returnable quantity is ${max}`}); setShowToaster(true);
             }
        }

        newItems[index][field] = value;
        
        // Recalculate line total & tax
        const qty = parseFloat(newItems[index].return_quantity) || 0;
        const price = parseFloat(newItems[index].return_price) || 0;
        let baseTotal = qty * price;
        
        const newAdjs = (newItems[index].adjustments || []).map(adj => {
             if (adj.percentage) {
                  adj.amount = ((baseTotal * parseFloat(adj.percentage)) / 100).toFixed(2);
             }
             return adj;
        });
        const adjTotal = newAdjs.reduce((sum, adj) => sum + (parseFloat(adj.amount)||0), 0);
        
        newItems[index].line_total = (baseTotal + adjTotal).toFixed(2);
        newItems[index].adjustments = newAdjs;

        setSr(prev => ({ 
            ...prev, 
            items: newItems,
            total_amount: newItems.reduce((sum, item) => sum + parseFloat(item.line_total||0), 0)
        }));
    };

    // --- Tax Handlers ---
    const addItemTax = (index) => {
        const newItems = [...sr.items];
        newItems[index].adjustments.push({ hoa_id: null, percentage: '', amount: '', is_subtraction: false });
        setSr({...sr, items: newItems});
    };
    const removeItemTax = (itemIndex, adjIndex) => {
        const newItems = [...sr.items];
        newItems[itemIndex].adjustments = newItems[itemIndex].adjustments.filter((_, i) => i !== adjIndex);
        handleItemChange(itemIndex, 'return_quantity', newItems[itemIndex].return_quantity); // Recalculate
    };
    const handleTaxChange = (itemIndex, adjIndex, field, value) => {
         const newItems = [...sr.items];
         newItems[itemIndex].adjustments[adjIndex][field] = value;
         handleItemChange(itemIndex, 'return_quantity', newItems[itemIndex].return_quantity); // Recalculate
    };

    // --- Save ---
    const handleSave = () => {
        if (saving || isViewOnly) return;
        setSaving(true); setErrors({});
        
        const validItems = sr.items.filter(i => parseFloat(i.return_quantity) > 0);
        if (validItems.length === 0) {
             setToaster({type: 'danger', heading: 'Error', message: 'Please enter a return quantity for at least one item.'}); setShowToaster(true); 
             setSaving(false); return;
        }

        createSalesReturn({
            ...sr,
            return_date: format(sr.return_date, 'yyyy-MM-dd'),
            items: validItems
        }, (res) => {
            setSaving(false);
            if (res.sales_return) {
                 navigate('/franchisee_user/sales/sales-returns', { state: { message: 'Sales Return created successfully.' } });
            } else {
                 handleApiError(res, setErrors);
            }
        });
    };

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main className="container-fluid">
                <AppBreadCrumb title={formType === 'create' ? 'New Sales Return' : 'View Sales Return'} />
                {loading && <div className="loader_box"><div className="loader_14"></div></div>}
                
                <div className="card">
                    <div className="card-header d-flex justify-content-between">
                        <h5>{sr.sales_return_number ? `Return #${sr.sales_return_number}` : 'Create Sales Return'}</h5>
                        {sr.source_info && <span className="badge bg-info text-dark align-self-center">Against: {sr.source_info}</span>}
                    </div>
                    <div className="card-body">
                        <form onSubmit={e => e.preventDefault()}>
                            {/* --- Header Fields --- */}
                            <div className="row mb-3">
                                <div className="col-md-4 mb-3">
                                    <label className="form-label">Customer *</label>
                                    <Select 
                                        options={partyOptions} 
                                        value={partyOptions.find(o => o.value === sr.party_id)} 
                                        onChange={opt => setSr({...sr, party_id: opt?.value, sales_invoice_id: null, items: [], source_info: null})}
                                        isDisabled={isViewOnly || !!sourceInvoiceId} // Lock if pre-loaded from URL
                                        placeholder="Select Customer" 
                                    />
                                    <span className="text-danger small">{errors.party_id}</span>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <label className="form-label">Against Invoice *</label>
                                    <Select 
                                        options={invoiceOptions} 
                                        value={invoiceOptions.find(o => o.value == sr.sales_invoice_id)}
                                        onChange={handleInvoiceChange}
                                        isDisabled={!sr.party_id || isViewOnly || !!sourceInvoiceId} 
                                        placeholder={sr.party_id ? "Select Invoice" : "Select Customer first"} 
                                        noOptionsMessage={() => "No eligible invoices found"} 
                                    />
                                    <span className="text-danger small">{errors.sales_invoice_id}</span>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <label className="form-label">Return Date *</label>
                                    <DatePicker selected={sr.return_date} onChange={d => setSr({...sr, return_date: d})} className="form-control" dateFormat="yyyy-MM-dd" disabled={isViewOnly}/>
                                    <span className="text-danger small">{errors.return_date}</span>
                                </div>
                                <div className="col-12 mb-3">
                                    <label className="form-label">Reason</label>
                                    <input className="form-control" name="reason" value={sr.reason} onChange={e => setSr({...sr, reason: e.target.value})} disabled={isViewOnly} placeholder="Optional reason..."/>
                                </div>
                            </div>

                            {/* --- Items Table --- */}
                            <h6 className="mb-2">Items to Return</h6>
                            <div className="table-responsive mb-3">
                                <table className="table table-bordered table-sm">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{width: '30%'}}>Product</th>
                                            <th style={{width: '15%'}} className="text-end">Return Qty</th>
                                            <th style={{width: '10%'}}>Unit</th>
                                            <th style={{width: '15%'}} className="text-end">Return Price</th>
                                            <th style={{width: '15%'}} className="text-end">Total Credit</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sr.items.map((item, i) => (
                                            <React.Fragment key={i}>
                                                <tr>
                                                    <td className="align-middle">
                                                        <span className="fw-500">{item.product_name || `Item #${item.product_id}`}</span>
                                                    </td>
                                                    <td>
                                                        <div className="input-group input-group-sm">
                                                            <input type="number" className="form-control text-end" value={item.return_quantity} 
                                                                onChange={e => handleItemChange(i, 'return_quantity', e.target.value)} 
                                                                disabled={isViewOnly} placeholder="0" min="0" />
                                                            {item.max_return_quantity !== null && !isViewOnly &&
                                                                <span className="input-group-text text-muted" style={{fontSize: '0.75rem'}}>
                                                                    / {item.max_return_quantity}
                                                                </span>
                                                            }
                                                        </div>
                                                    </td>
                                                    <td className="align-middle"><small>{item.uom_name || '-'}</small></td>
                                                    <td>
                                                        <input type="number" className="form-control form-control-sm text-end" value={item.return_price} 
                                                            onChange={e => handleItemChange(i, 'return_price', e.target.value)} 
                                                            disabled={isViewOnly} readOnly /> {/* Price usually fixed from invoice */}
                                                    </td>
                                                    <td className="text-end align-middle fw-bold">
                                                        {item.line_total || '0.00'}
                                                    </td>
                                                </tr>
                                                {/* Tax Row */}
                                                {(parseFloat(item.return_quantity) > 0 || isViewOnly) && (
                                                    <tr>
                                                        <td colSpan={5} className="bg-light p-2 border-bottom">
                                                            <div className="d-flex align-items-center mb-1">
                                                                <small className="text-muted me-2">Taxes to Reverse:</small>
                                                                {!isViewOnly && <button className="btn btn-xs btn-outline-primary py-0" onClick={() => addItemTax(i)}>+ Add Tax</button>}
                                                            </div>
                                                            {item.adjustments.map((adj, j) => (
                                                                <div className="row gx-1 mb-1" key={j} style={{maxWidth: '500px'}}>
                                                                     <div className="col-6">
                                                                         <Select options={gstOptions} value={gstOptions.find(o => o.value === adj.hoa_id)} onChange={sel => handleTaxChange(i, j, 'hoa_id', sel?.value)} placeholder="Select Tax" className="form-control-sm p-0" isDisabled={isViewOnly}
                                                                             styles={{ control: base => ({...base, minHeight: '28px', height: '28px'}), valueContainer: base => ({...base, height: '28px', padding: '0 6px'}), indicatorsContainer: base => ({...base, height: '28px'}) }}
                                                                         />
                                                                     </div>
                                                                     <div className="col-3"><div className="input-group input-group-sm"><input className="form-control" placeholder="%" value={adj.percentage} onChange={e => handleTaxChange(i, j, 'percentage', e.target.value)} disabled={isViewOnly}/><span className="input-group-text">%</span></div></div>
                                                                     <div className="col-3"><input className="form-control form-control-sm text-end" placeholder="Amt" value={adj.amount} readOnly /></div>
                                                                     <div className="col-auto">{!isViewOnly && <button className="btn btn-xs btn-link text-danger py-0" onClick={() => removeItemTax(i,j)}>&times;</button>}</div>
                                                                </div>
                                                            ))}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                        {sr.items.length === 0 && !loading && 
                                            <tr><td colSpan={5} className="text-center text-muted p-4">Please select a Customer and Invoice to load items.</td></tr>
                                        }
                                    </tbody>
                                </table>
                            </div>

                            {/* --- Footer Totals --- */}
                            <div className="row justify-content-end border-top pt-3">
                                <div className="col-md-4 text-end">
                                    <h5>Total Credit Note Value: <span className="text-primary">{parseFloat(sr.total_amount || 0).toFixed(2)}</span></h5>
                                </div>
                            </div>

                            {/* --- Action Buttons --- */}
                            <div className="text-end mt-3">
                                {!isViewOnly ? (
                                    <>
                                    <button className="btn btn-primary me-2" onClick={handleSave} disabled={saving || sr.total_amount <= 0}>
                                        {saving ? 'Saving...' : 'Create Sales Return'}
                                    </button>
                                     <button className="d-none  d-md-inline-block btn btn-light-reddit b-r-22 d-inline-flex-center" type="button" onClick={() => navigate('/franchisee_user/sales/sales-returns')} >
                          
                            Go Back</button>
                                    </>
                                    
                                ) : (
                                    <button className="btn  btn btn-light-reddit" onClick={() => navigate('/franchisee_user/sales/sales-returns')}>Close View</button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            </main>
        </>
    );
};

export default SalesReturnForm;